import { Injectable } from '@angular/core';
import 'rxjs/add/operator/map';


import { CONSTANT_VALUES } from '../../constants/constant-values';
import { HttpInterceptor } from '../../shared/index';
import { AppService } from '../../app.service';
import { environment } from '../../../environments/environment';
import { EventFacility } from './event-facility.interface';
import { CONSTANT_MESSAGES } from '../../constants/constants-messages';
import { UtilityFunctions } from '../../shared/utility-functions';



@Injectable()
export class EventService {
  private _appService;
  private utilityFunctions = new UtilityFunctions();



  constructor(private http: HttpInterceptor, appService: AppService) {
    this._appService = appService;
  }



  /**
   * Create event service API call
   *
   * @param {EventFacility} eventObject
   * @returns
   *
   * @memberOf EventService
   */
  addEvent(eventObject: EventFacility) {
    let url = CONSTANT_VALUES.URL_CREATE_OR_UPDATE_FACILITY;
    return new Promise((resolve, reject) => {
      this.http.post(url, eventObject).map((res) => res.json()).subscribe(data => {
        resolve(data);
      }, error => {
        reject(error);
      });
    });
  }



  /**
   * Update event service API call
   *
   * @param {EventFacility} event
   * @returns
   *
   * @memberOf EventService
   */
  updateEvent(event: EventFacility) {
    let url = CONSTANT_VALUES.URL_CREATE_OR_UPDATE_FACILITY;
    return new Promise((resolve, reject) => {
      this.http.put(url, event).map((res) => res.json()).subscribe(data => {
        resolve(data);
      }, error => {
        reject(error);
      });
    });
  }



  /**
   * Delete event service API call
   *
   * @param {any} event
   * @returns
   *
   * @memberOf EventService
   */
  deleteEvent(event: { facilityId: string, vendorId: string }) {
    let url = CONSTANT_VALUES.URL_DELETE_FACILITY;
    return new Promise((resolve, reject) => {
      this.http.post(url, event).map((res) => res.json()).subscribe(data => {
        resolve(data);
      }, error => {
        reject(error);
      });
    });
  }



  /**
   * Validate event timing functionality (cleaned for events only)
   *
   * @param {any} timings
   * @param {string} category
   * @returns
   *
   * @memberOf EventService
   */
  validateEventFunctionality(timings: { openTime: string, closeTime: string }, category: string) {
    let response = {
      status: 300,
      message: ''
    };


    // Avoid side effects by working with a copy
    const cleanTimings = {
      openTime: timings.openTime.replace(/:00:00/g, ''),
      closeTime: timings.closeTime.replace(/:00:00/g, '')
    };



    let errMessage = '';



    // Validate start time is before end time
    if (cleanTimings.openTime > cleanTimings.closeTime) {
      errMessage = CONSTANT_MESSAGES.ERROR_IN_EVENT_START_TIME_AND_END_TIME;
      response.status = 301;
      response.message = errMessage;
      return response;
    }



    // Validate start time is not equal to end time
    if (cleanTimings.openTime === cleanTimings.closeTime) {
      errMessage = CONSTANT_MESSAGES.ERROR_IN_FACILITY_TIME_DURATION;
      response.status = 309;
      response.message = errMessage;
      return response;
    }



    // Event-specific category validation
    if (category !== 'Events') {
      response.status = 400;
      errMessage = 'Invalid category for event. Must be "Events".';
      response.message = errMessage;
      return response;
    }



    return response;
  }



  /**
   * Validate event date
   *
   * @param {string} eventDate
   * @returns
   *
   * @memberOf EventService
   */
  validateEventDate(eventDate: string) {
    let response = {
      status: 300,
      message: ''
    };



    if (!eventDate || eventDate.trim() === '') {
      response.status = 400;
      response.message = 'Event date is required';
      return response;
    }



    const selectedDate = new Date(eventDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);



    // Check if event date is in the past (same day allowed)
    if (selectedDate < today) {
      response.status = 400;
      response.message = 'Event date cannot be in the past';
      return response;
    }



    // Check if event date is too far in future (2 years max)
    const maxFutureDate = new Date();
    maxFutureDate.setFullYear(maxFutureDate.getFullYear() + 2);



    if (selectedDate > maxFutureDate) {
      response.status = 400;
      response.message = 'Event date cannot be more than 2 years in the future';
      return response;
    }



    return response;
  }



  /**
   * Validate event capacity (supports "unlimited")
   *
   * @param {string} capacity
   * @returns
   *
   * @memberOf EventService
   */
  validateEventCapacity(capacity: string) {
    let response = {
      status: 300,
      message: ''
    };


    if (!capacity || capacity.trim() === '') {
      response.status = 400;
      response.message = 'Event capacity is required';
      return response;
    }



    if (capacity.toLowerCase() !== 'unlimited') {
      const capacityNum = parseInt(capacity, 10);
      if (isNaN(capacityNum) || capacityNum <= 0) {
        response.status = 400;
        response.message = 'Event capacity must be a positive number or "unlimited"';
        return response;
      }
    }



    return response;
  }

  validateMaxBookingPerSlot(maxBookings: string, eventCapacity: string) {
    let response = {
      status: 300,
      message: ''
    };


    if (!maxBookings || maxBookings.trim() === '') {
      response.status = 400;
      response.message = 'Event Max Bookings is required';
      return response;
    }

    const maxBookingNum = parseInt(maxBookings, 10);
    if (isNaN(maxBookingNum) || maxBookingNum <= 0) {
      response.status = 400;
      response.message = 'Event Max Bookings must be a positive number';
      return response;
    }
    if (!eventCapacity || eventCapacity.trim() === '') {
      response.status = 400;
      response.message = 'Event Capacity is required to validate Max Bookings';
      return response;
    }

    const eventCapacityNum = eventCapacity.toLowerCase() === 'unlimited' ? 999999 : parseInt(eventCapacity, 10);

    if (isNaN(maxBookingNum) || maxBookingNum > eventCapacityNum) {
      response.status = 400;
      response.message = 'Event Max Bookings must be a positive number and less than or equal to Event Capacity';
      return response;
    }

    return response;
  }


  /**
   * Validate event address (enhanced address fields)
   *
   * @param {any} addressData
   * @returns
   *
   * @memberOf EventService
   */
  validateEventAddress(addressData: any) {
    let response = {
      status: 300,
      message: ''
    };



    if (!addressData.addressLine1 || addressData.addressLine1.trim() === '') {
      response.status = 400;
      response.message = 'Address Line 1 is required';
      return response;
    }



    if (addressData.addressLine1.trim().length < 5) {
      response.status = 400;
      response.message = 'Address Line 1 must be at least 5 characters';
      return response;
    }



    if (!addressData.pincode || addressData.pincode.trim() === '') {
      response.status = 400;
      response.message = 'Pincode is required';
      return response;
    }



    if (addressData.pincode.trim().length < 3) {
      response.status = 400;
      response.message = 'Pincode must be at least 3 characters';
      return response;
    }



    if (!addressData.country || addressData.country.trim() === '') {
      response.status = 400;
      response.message = 'Country is required';
      return response;
    }



    return response;
  }



  /**
   * Generate full location string from address components
   *
   * @param {any} addressData
   * @returns {string}
   *
   * @memberOf EventService
   */
  generateFullLocation(addressData: any): string {
    const addressParts = [];



    if (addressData.addressLine1) addressParts.push(addressData.addressLine1.trim());
    if (addressData.addressLine2) addressParts.push(addressData.addressLine2.trim());
    if (addressData.addressLine3) addressParts.push(addressData.addressLine3.trim());
    if (addressData.pincode) addressParts.push(addressData.pincode.trim());
    if (addressData.country) addressParts.push(addressData.country.trim());



    return addressParts.join(', ');
  }



  /**
   * Validate 5-tier event pricing
   *
   * @param {any} pricingData
   * @returns
   *
   * @memberOf EventService
   */
  validateEventPricing(pricingData: any) {
    let response = {
      status: 300,
      message: ''
    };



    if (!pricingData.activityCharges) {
      return response; // No pricing configured is valid
    }



    const pricingTiers = ['member', 'nonMember', 'guest', 'corpMember', 'corpNonMember'];



    for (let tier of pricingTiers) {
      const pricingTier = pricingData.activityCharges[tier];
      if (pricingTier && pricingTier.enabled) {
        if (!pricingTier.amount || pricingTier.amount.toString().trim() === '') {
          response.status = 400;
          response.message = `${tier.charAt(0).toUpperCase() + tier.slice(1)} amount is required when pricing is enabled`;
          return response;
        }



        const amount = parseFloat(pricingTier.amount);
        if (isNaN(amount) || amount < 0) {
          response.status = 400;
          response.message = `${tier.charAt(0).toUpperCase() + tier.slice(1)} amount must be a valid positive number`;
          return response;
        }
      }
    }



    return response;
  }



  /**
   * Validate event type selection
   *
   * @param {string} eventType
   * @returns
   *
   * @memberOf EventService
   */
  validateEventType(eventType: string) {
    let response = {
      status: 300,
      message: ''
    };



    if (!eventType || eventType.trim() === '') {
      response.status = 400;
      response.message = 'Event type is required';
      return response;
    }



    // FIXED: Synchronized with event.component.ts
    const allowedEventTypes = [
      'Movies', 'Functions', 'Talk Shows', 'Festive Family Get Togethers',
      'Dining Events', 'Cocktail Party', 'Workshops', 'Conferences',
      'Sports Events', 'Cultural Events', 'Business Meetings', 'Training Sessions',
      'Networking Events', 'Product Launches', 'Award Ceremonies', 'Other'
    ];



    if (allowedEventTypes.indexOf(eventType) === -1) {
      response.status = 400;
      response.message = 'Invalid event type selected';
      return response;
    }



    return response;
  }



  /**
   * Validate waitlist settings
   *
   * @param {any} waitlistData
   * @returns
   *
   * @memberOf EventService
   */
  validateWaitlist(waitlistData: any) {
    let response = {
      status: 300,
      message: ''
    };



    if (waitlistData.isWaitlistEnabled) {
      if (!waitlistData.waitlistCount || waitlistData.waitlistCount.toString().trim() === '') {
        response.status = 400;
        response.message = 'Waitlist count is required when waitlist is enabled';
        return response;
      }



      const waitlistCount = parseInt(waitlistData.waitlistCount.toString(), 10);
      if (isNaN(waitlistCount) || waitlistCount <= 0 || waitlistCount > 50) {
        response.status = 400;
        response.message = 'Waitlist count must be between 1 and 50';
        return response;
      }
    }



    return response;
  }



  /**
   * Clean and prepare event data before saving (streamlined for events)
   *
   * @param {EventFacility} event
   * @returns {EventFacility}
   *
   * @memberOf EventService
   */
  prepareEventForSave(event: EventFacility): EventFacility {
    // Clean up text fields
    event.name = (event.name + '').trim();
    event.notes = (event.notes + '').trim();
    event.eventType = (event.eventType + '').trim();



    // Clean up address fields
    event.addressLine1 = (event.addressLine1 || '').trim();
    event.addressLine2 = (event.addressLine2 || '').trim();
    event.addressLine3 = (event.addressLine3 || '').trim();
    event.pincode = (event.pincode || '').trim();
    event.country = (event.country || '').trim();



    // Generate full location
    event.location = this.generateFullLocation(event);



    // Set event-specific defaults
    event.category = 'Events';
    event.isPeakTimeEnabled = false;
    event.isBreakTimeEnabled = false;
    event.isSlotsLocked = true;
    event.slotDuration = '0'; // Events don't use slot duration



    // Clean up capacity
    if (event.maxBookingsPerSlot === '') {
      event.maxBookingsPerSlot = '1';
    }



    if (event.maxBookingsPerSlot &&
      event.maxBookingsPerSlot.toLowerCase() === 'unlimited') {
      event.maxBookingsPerSlot = '999999';
    }



    // Clear facility-specific fields not used by events
    event.unitNames = [];
    event.noOfUnits = '';
    event.breakTimings = [];
    event.peekTimings = [];
    event.maxWeekdayBooking = '';
    event.maxWeekendBooking = '';
    event.maxBookingHoursWeekday = '';
    event.maxBookingHoursWeekend = '';
    event.maxBookingHoursMonth = '';
    event.holdBookingDays = '';
    event.peakUnitsCount = '';



    return event;
  }



  /**
   * Validate complete event object (streamlined for events only)
   *
   * @param {EventFacility} event
   * @returns
   *
   * @memberOf EventService
   */
  validateCompleteEvent(event: EventFacility) {
    let response = {
      status: 300,
      message: '',
      errors: []
    };



    // Validate event type
    const eventTypeValidation = this.validateEventType(event.eventType);
    if (eventTypeValidation.status !== 300) {
      response.errors.push(eventTypeValidation.message);
    }



    // Validate event name
    if (!event.name || event.name.trim() === '' || event.name.trim().length < 3) {
      response.errors.push('Event name must be at least 3 characters');
    }



    // Validate event date
    const dateValidation = this.validateEventDate(event.date);
    if (dateValidation.status !== 300) {
      response.errors.push(dateValidation.message);
    }



    // Validate address
    const addressValidation = this.validateEventAddress(event);
    if (addressValidation.status !== 300) {
      response.errors.push(addressValidation.message);
    }



    // Validate max bookings per slot
    const maxBookingPerSlotValidation = this.validateMaxBookingPerSlot(event.maxBookingsPerSlot, event.eventCapacity);
    if (maxBookingPerSlotValidation.status !== 300) {
      response.errors.push(maxBookingPerSlotValidation.message);
    }

    // Validate capacity
    const capacityValidation = this.validateEventCapacity(event.eventCapacity);
    if (capacityValidation.status !== 300) {
      response.errors.push(capacityValidation.message);
    }

    // Validate timings
    const timingValidation = this.validateEventFunctionality(event.timings, event.category);
    if (timingValidation.status !== 300) {
      response.errors.push(timingValidation.message);
    }



    // Validate pricing if enabled
    const pricingValidation = this.validateEventPricing(event);
    if (pricingValidation.status !== 300) {
      response.errors.push(pricingValidation.message);
    }



    // Validate waitlist if enabled
    const waitlistValidation = this.validateWaitlist(event);
    if (waitlistValidation.status !== 300) {
      response.errors.push(waitlistValidation.message);
    }



    // Set response status and message
    if (response.errors.length > 0) {
      response.status = 400;
      response.message = response.errors.join('; ');
    }



    return response;
  }



  /**
   * Get upcoming events for a vendor (Refactored to use Promise.then)
   */
  getUpcomingEvents(vendorObj: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this._appService.getEventsByVendor(vendorObj)
        .then((result: any) => {
          if (result.status === 'SUCCESS') {
            // Filter for upcoming events only
            const currentDate = new Date();
            currentDate.setHours(0, 0, 0, 0);

            const upcomingEvents = result.events.filter(event => {
              if (!event.date) {
                return false;
              }

              const eventDate = new Date(event.date);
              eventDate.setHours(0, 0, 0, 0);

              return eventDate >= currentDate;
            });

            resolve({
              status: 'SUCCESS',
              events: upcomingEvents
            });
          } else {
            resolve({
              status: 'ERROR',
              events: [],
              message: result.message || 'No events found'
            });
          }
        })
        .catch(error => {
          // Reject the promise with the error
          reject(error);
        });
    });
  }




  /**
* Get club events using existing facility endpoint (filtered by category)
*/
  getClubEvents(vendorObj: any) {
    // Use existing facility endpoint constant
    let url = CONSTANT_VALUES.URL_FACILITY_LIST_BY_VENDOR; // /api/facility/getclubfacilitybyvendor



    return new Promise((resolve, reject) => {
      this.http.post(url, vendorObj).map((res) => res.json()).subscribe(data => {
        // Filter only Events category from the response
        if (data.status === 'SUCCESS' && data.facilityData) {
          const eventsData = data.facilityData.filter(category =>
            category.categoryName === 'Events'
          );



          // Transform response to match expected format
          data.eventsData = eventsData;
          // Keep original structure for compatibility
          data.facilityData = eventsData;
        }
        resolve(data);
      }, error => {
        reject(error);
      });
    });
  }

/**
 * Update group(s)
 * - Single: pass { groupId, vendorId, newName?, description?, activities? }
 * - Multiple: pass { groupIds: string[], vendorId, activities? }
 *   (newName/description are ignored by backend for multi per your server logic)
 */
updateGroup(groupObj: {
  vendorId: string;
  groupId?: string;          // single
  groupIds?: string[];       // multiple
  newName?: string;
  description?: string;
  activities?: string[];
}): Promise<any> {
  const url = CONSTANT_VALUES.URL_UPDATE_GROUP;
  return new Promise((resolve, reject) => {
    this.http.post(url, groupObj)
      .map(res => res.json())
      .subscribe(
        data => resolve(data),
        err => reject(err)
      );
  });
}

/**
 * Convenience wrapper when you always have multiple IDs
 */
updateGroups(groupIds: string[], vendorId: string, activities?: string[]): Promise<any> {
  return this.updateGroup({ groupIds, vendorId, activities });
}


}



// WEBPACK FOOTER //
// ./src/app/facility/event/event.service.ts